import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import FilterPanel, { type FilterState } from "@/pages/category/components/FilterPanel";
import { getByType, isMediaType, parseList, typeMeta } from "@/lib/media";
import { usePageMeta } from "@/hooks/usePageMeta";

type SortKey = "latest" | "hot" | "rating";

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "按时间", icon: "ri-calendar-line" },
  { key: "hot", label: "按热度", icon: "ri-fire-line" },
  { key: "rating", label: "按评分", icon: "ri-star-line" },
];

const PAGE_SIZE = 9;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);

  const validType = isMediaType(type) ? type : null;
  const meta = validType ? typeMeta[validType] : null;

  const filters: FilterState = useMemo(
    () => ({
      genres: parseList(searchParams.get("genre")),
      regions: parseList(searchParams.get("region")),
      years: parseList(searchParams.get("year")).map(Number).filter((year) => !Number.isNaN(year)),
    }),
    [searchParams],
  );

  const sortParam = searchParams.get("sort");
  const sort: SortKey =
    sortParam === "hot" || sortParam === "rating" ? sortParam : "latest";

  usePageMeta({
    title: meta ? `${meta.label} - 蜜桃33影视资源` : "影视分类 - 蜜桃33",
    description: meta
      ? `蜜桃33精选${meta.label}影视剧，全面分类筛选，助您轻松观影。`
      : "蜜桃33影视分类，提供电影、电视剧与综艺的全面筛选。",
    keywords: meta
      ? `蜜桃33,${meta.label},影视资源,${meta.label}推荐,${meta.label}筛选`
      : "蜜桃33,影视资源",
  });

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type, searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [type]);

  useEffect(() => {
    if (!drawerOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [drawerOpen]);

  const updateParams = (next: URLSearchParams) => {
    setSearchParams(next, { replace: false });
  };

  const setSort = (key: SortKey) => {
    const next = new URLSearchParams(searchParams);
    if (key === "latest") next.delete("sort");
    else next.set("sort", key);
    updateParams(next);
  };

  const toggleFilter = (group: "genres" | "regions" | "years", value: string) => {
    const next = new URLSearchParams(searchParams);
    const paramKey = group === "genres" ? "genre" : group === "regions" ? "region" : "year";
    const current = parseList(next.get(paramKey));
    const updated = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    if (updated.length) next.set(paramKey, updated.join(","));
    else next.delete(paramKey);
    updateParams(next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("genre");
    next.delete("region");
    next.delete("year");
    updateParams(next);
  };

  const results = useMemo(() => {
    if (!validType) return [];
    const base = getByType(validType).filter((item) => {
      const genreMatch =
        filters.genres.length === 0 || item.genres.some((genre) => filters.genres.includes(genre));
      const regionMatch = filters.regions.length === 0 || filters.regions.includes(item.region);
      const yearMatch = filters.years.length === 0 || filters.years.includes(item.year);
      return genreMatch && regionMatch && yearMatch;
    });

    return [...base].sort((a, b) => {
      if (sort === "hot") return b.heat - a.heat;
      if (sort === "rating") return b.rating - a.rating;
      return b.year - a.year || b.heat - a.heat;
    });
  }, [validType, filters, sort]);

  if (!validType || !meta) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex w-full flex-1 items-center justify-center px-4 py-24">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-600">
              <i className="ri-error-warning-line text-2xl" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">
              分类不存在
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-foreground-500">
              你访问的影视分类不存在，可以从下面回到首页或选择其他分类。
            </p>
            <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
              <Link
                to="/"
                className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                返回首页
              </Link>
              <Link
                to="/category/movie"
                className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-5 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              >
                去看电影
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const activeCount = filters.genres.length + filters.regions.length + filters.years.length;
  const shown = results.slice(0, visible);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="w-full flex-1">
        <div className="border-b border-background-200 bg-background-100 px-4 py-8 md:px-8 md:py-10">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300" />
            <span className="text-foreground-800">{meta.label}</span>
          </nav>

          <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-primary-600">
                {validType === "movie" ? "Movie library" : validType === "tv" ? "Series library" : "Variety library"}
              </p>
              <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                {meta.subtitle}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {meta.description}
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs text-foreground-500">
              <span className="rounded-md bg-background-50 px-3 py-2">
                共 <span className="font-semibold text-primary-600">{results.length}</span> 部作品
              </span>
              {activeCount > 0 && (
                <span className="rounded-md bg-primary-50 px-3 py-2 text-primary-700">
                  {activeCount} 个筛选条件
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="w-full px-4 py-8 md:px-8 md:py-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            <aside className="hidden w-64 shrink-0 lg:block">
              <div className="sticky top-24">
                <FilterPanel
                  type={validType}
                  filters={filters}
                  onToggle={toggleFilter}
                  onClear={clearFilters}
                />
              </div>
            </aside>

            <div className="min-w-0 flex-1">
              <div className="mb-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-700 lg:hidden"
                >
                  <i className="ri-equalizer-line" />
                  筛选
                  {activeCount > 0 && (
                    <span className="rounded-full bg-primary-500 px-1.5 text-[10px] font-medium text-background-50">
                      {activeCount}
                    </span>
                  )}
                </button>

                <div className="flex items-center gap-1 rounded-md bg-background-100 p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSort(option.key)}
                      className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                        sort === option.key
                          ? "bg-background-50 text-primary-600"
                          : "text-foreground-500 hover:text-foreground-800"
                      }`}
                    >
                      <i className={`${option.icon} text-[13px]`} />
                      {option.label}
                    </button>
                  ))}
                </div>

                <span className="ml-auto hidden text-xs text-foreground-400 sm:inline">
                  当前显示 {shown.length} / {results.length}
                </span>
              </div>

              {activeCount > 0 && (
                <div className="mb-5 flex flex-wrap items-center gap-2">
                  {filters.genres.map((genre) => (
                    <button
                      key={`g-${genre}`}
                      type="button"
                      onClick={() => toggleFilter("genres", genre)}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-primary-100 px-3 py-1.5 text-xs text-primary-700 transition-colors hover:bg-primary-200"
                    >
                      {genre}
                      <i className="ri-close-line text-[13px]" />
                    </button>
                  ))}
                  {filters.regions.map((region) => (
                    <button
                      key={`r-${region}`}
                      type="button"
                      onClick={() => toggleFilter("regions", region)}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {region}
                      <i className="ri-close-line text-[13px]" />
                    </button>
                  ))}
                  {filters.years.map((year) => (
                    <button
                      key={`y-${year}`}
                      type="button"
                      onClick={() => toggleFilter("years", String(year))}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-accent-100 px-3 py-1.5 text-xs text-accent-800 transition-colors hover:bg-accent-200"
                    >
                      {year}
                      <i className="ri-close-line text-[13px]" />
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="cursor-pointer px-2 text-xs text-foreground-400 underline-offset-2 hover:text-primary-600 hover:underline"
                  >
                    清空全部
                  </button>
                </div>
              )}

              {shown.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 md:gap-x-5 md:gap-y-9 xl:grid-cols-4">
                    {shown.map((item) => (
                      <MovieCard key={item.id} item={item} />
                    ))}
                  </div>

                  {visible < results.length && (
                    <div className="mt-10 flex flex-col items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                        className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                      >
                        加载更多
                        <i className="ri-arrow-down-line" />
                      </button>
                      <span className="text-[11px] text-foreground-400">
                        已展示 {shown.length} 部，剩余 {results.length - shown.length} 部
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400">
                    <i className="ri-search-eye-line text-2xl" />
                  </span>
                  <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                    没有符合条件的内容
                  </h2>
                  <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-foreground-500">
                    当前的筛选组合暂时没有匹配到作品，试试减少一个条件，或者看看该分类的全部内容。
                  </p>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="mt-5 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    清空筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选面板"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 cursor-pointer bg-foreground-950/40"
          />
          <div className="absolute inset-y-0 left-0 flex w-[86%] max-w-sm flex-col overflow-y-auto bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950">筛选</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-700"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel
              type={validType}
              filters={filters}
              onToggle={toggleFilter}
              onClear={clearFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 inline-flex h-11 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-background-50"
            >
              查看 {results.length} 部结果
            </button>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}